import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/pt';
import i18next from 'i18next';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import { localTenant } from '@/services/session-service';
import { Person } from '@turizi/shared';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

const getCurrentLang = () => {
  return i18next.language;
}

export const formatPrice = (price: number | null | undefined, options?: { signDisplay?: 'auto' | 'always' | 'exceptZero', currency?: string }) => {
  if (price === null || price === undefined) return '_';
  // Use es-AR locale for Latin American formatting (1.100 instead of 1,100)
  const locale = getCurrentLang() === 'es' ? 'es-AR' : getCurrentLang();

  const formatterOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    signDisplay: options?.signDisplay || "auto"
  };

  if (options?.currency) {
    const rawCurrency = options.currency.trim();
    if (/^[A-Za-z]{3}$/.test(rawCurrency)) {
      try {
        const fullOptions: Intl.NumberFormatOptions = {
          ...formatterOptions,
          style: 'currency',
          currency: rawCurrency.toUpperCase(),
        };
        return new Intl.NumberFormat(locale, fullOptions).format(price);
      } catch {
        // Fall back to manual currency label if Intl doesn't recognize currency code
      }
    }
    const formattedNum = new Intl.NumberFormat(locale, formatterOptions).format(price);
    return `${rawCurrency || ''} ${formattedNum}`.trim();
  }
  const amount = new Intl.NumberFormat(locale, formatterOptions).format(price);
  return `${amount} ${options?.currency || ''}`.trim();
}

export const applyDiscount = (amount: number, discountPercentage?: number | null): number => {
  if (!amount) return 0;
  if (!discountPercentage || discountPercentage <= 0) return amount;
  return Math.ceil(amount * (1 - discountPercentage / 100));
};

export const formatExchangeRate = (price: number, maxDigits: number = 4) => {
  if (!price && price !== 0) return '-';
  // Use es-AR locale for Latin American formatting (1.100 instead of 1,100)
  const locale = getCurrentLang() === 'es' ? 'es-AR' : getCurrentLang();
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDigits,
  }).format(price);
}

export const getDaysArrayBetweenDates = (startDate: string, endDate: string) => {
  const days = [];
  for (let d = new Date(startDate); d <= new Date(endDate); d.setDate(d.getDate() + 1)) {
    days.push(d.toISOString().split('T')[0]);
  }
  return days;
}

export const getDateArrayBetweenDates = (startDate: string, endDate: string) => {
  const days = [];
  for (let d = new Date(startDate); d <= new Date(endDate); d.setDate(d.getDate() + 1)) {
    days.push(d);
  }
  return days;
}

export const yesterdayStart = () => {
  return dayjs().subtract(1, 'day').startOf('day').format('YYYY-MM-DD');
}

export const todayStart = () => {
  return dayjs().startOf('day').format('YYYY-MM-DD');
}

export const endOfMonth = () => {
  return dayjs().endOf('month').format('YYYY-MM-DD');
}

export const tomorrowStart = () => {
  return dayjs().add(1, 'day').startOf('day').format('YYYY-MM-DD');
}

export const next10DaysStart = () => {
  return dayjs().add(10, 'day').startOf('day').format('YYYY-MM-DD');
}

export const startOfYear = () => {
  return dayjs().startOf('year').format('YYYY-MM-DD');
}

export const endOfYear = () => {
  return dayjs().endOf('year').format('YYYY-MM-DD');
}

// "DD MMM YYYY - HH:mm"
export const formatDate = (date: string | Date | null): string => {
  if (!date) return '--/--/-- - --:--';
  return dayjs(date).tz('utc').locale(getCurrentLang()).format('DD MMM YYYY - HH:mm');
}

export const formatWeekDayShort = (date: string | Date | null, placeholder?: string) => {
  if (!date) return placeholder !== undefined ? placeholder : '-- / ---';
  return dayjs(date).tz('utc').locale(getCurrentLang()).format('dddd, DD MMM');
}

export const formatDayShort = (date: string | Date | null, placeholder?: string) => {
  if (!date) return placeholder !== undefined ? placeholder : '-- / ---';
  return dayjs(date).tz('utc').locale(getCurrentLang()).format('DD MMM');
}

export const formatDay = (date: string | Date | null, timezone?: string, placeholder?: string) => {
  if (!date) return placeholder !== undefined ? placeholder : '-- / --- / ----';
  if (timezone) {
    return dayjs(date).tz(timezone).locale(getCurrentLang()).format('DD MMM, YYYY');
  }
  return dayjs(date).locale(getCurrentLang()).format('DD MMM, YYYY');
}

export const formatDayLong = (date: string | Date | null, placeholder?: string) => {
  if (!date) return placeholder !== undefined ? placeholder : '--/---/----';
  return dayjs(date).tz('utc').locale(getCurrentLang()).format('DD MMMM, YYYY');
}

export const formatDayLongTime = (date: string | Date | null) => {
  if (!date) return '';
  return dayjs(date).locale(getCurrentLang()).format('DD MMM, YYYY - HH:mm');
}

export const formatRelativeTime = (date: string | Date | null) => {
  if (!date) return '';
  return dayjs(date).tz('utc').locale(getCurrentLang()).fromNow();
}


export const formatTime = (time: string, defaultValue?: string) => {
  if (!time) return defaultValue;
  return time.split(':').slice(0, 2).join(':');
}

export const formatToLocalTime = (isoString: string | null | undefined, timezone?: string): string => {
  if (!isoString) return '--:--';
  if (isoString.length === 5 && isoString.includes(':')) return isoString;
  if (isoString.includes('T') && !isoString.endsWith('Z') && !isoString.includes('+') && !isoString.includes('-')) {
    return isoString.split('T')[1].substring(0, 5);
  }
  try {
    if (timezone) {
      return dayjs(isoString).tz(timezone).format('HH:mm');
    }
    return dayjs(isoString).format('HH:mm');
  } catch (e) {
    return isoString.includes('T') ? isoString.split('T')[1].substring(0, 5) : isoString;
  }
}

export const formatToLocalDate = (isoString: string | null | undefined, timezone?: string): string => {
  if (!isoString) return '';
  if (isoString.includes('T') && !isoString.endsWith('Z') && !isoString.includes('+') && !isoString.includes('-')) {
    return isoString.split('T')[0];
  }
  try {
    if (timezone) {
      return dayjs(isoString).tz(timezone).format('YYYY-MM-DD');
    }
    return dayjs(isoString).format('YYYY-MM-DD');
  } catch (e) {
    return isoString.includes('T') ? isoString.split('T')[0] : isoString;
  }
}

export const getLocalizedWeekDays = () => {
  const days = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(2023, 0, i + 1); // Start from Sunday
    days.push(date.toLocaleDateString(undefined, { weekday: 'short' }));
  }
  return days;
}

export const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = -6; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    options.push({
      value: date.toISOString(),
      label: date.toLocaleDateString('default', { month: 'long', year: 'numeric' })
    });
  }
  return options;
}

export const getDaysInMonth = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

export const getFirstDayOfMonth = (date: Date) => {
  return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
}

export const formatDateKey = (year: number, month: number, day: number) => {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

export const combineArrObj = (key: string, arr: any[], source: any[]) => {
  if (source.length === 0) return { [key]: arr };
  const arrSource = source.flatMap(s => s[key]);
  const arrCopy = [...arr];
  arrSource.forEach(newAttr => {
    if (!arrCopy.some(attr => attr.key === newAttr.key)) {
      arrCopy.push(newAttr);
    }
  });
  return { [key]: arrCopy };
};

export const tagColor = (tag: string) => {
  switch (tag) {
    case 'driver':
      return 'red';
    case 'guide':
      return 'yellow';
    case 'coordinator':
      return 'orange';
    case 'sales':
      return 'yellow';
    case 'transfer':
      return 'green';
    case 'other':
      return 'gray';
    default:
      return 'blue';
  }
}


export const setWebTitle = (title: string) => {
  // const tenant = localTenant();
  document.title = title;
}

export const appendWebTitle = (title: string) => {
  if (!title || document.title.includes(title)) return;
  document.title = document.title + ` - ${title}`;
}

export const updateUrlParams = (params: Record<string, string>) => {
  const newParams = new URLSearchParams(window.location.search);
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      newParams.delete(key);
    } else {
      newParams.set(key, value);
    };
  });
  window.history.replaceState({}, '', `?${newParams.toString()}`);
}

export const getUrlParam = (key: string, defaultValue?: string): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get(key) || defaultValue;
}

// Icon and display name mapping for service attributes
export const attributeIconMap: Record<string, string> = {
  // Location & Geography
  'location': 'IconMapPin',
  'origin_city': 'IconMapPin',
  'destination_city': 'IconMapPin',
  'address': 'IconMapPin',
  'coordinates': 'IconMap',
  'region': 'IconWorld',
  'country': 'IconWorld',

  // Accommodation
  'capacity': 'IconUsers',
  'max_guests': 'IconUsers',
  'single_beds': 'IconBed',
  'double_beds': 'IconBed',
  'room_type': 'IconHome',
  'home_type': 'IconBuilding',
  'place_type': 'IconBuilding',
  'private_room': 'IconLock',
  'bath_private': 'IconBath',
  'amenities': 'IconSparkles',
  'jacuzzi': 'IconPool',
  'spacial_view': 'IconEye',
  'baths_number': 'IconBath',

  // Transportation
  'vehicle_type': 'IconCar',
  'duration': 'IconClock',
  'regular_schedule': 'IconCalendar',
  'private': 'IconLock',
  'shared': 'IconUsers',
  'reversible': 'IconArrowsRightLeft',

  // Tours & Activities
  'difficulty': 'IconMountain',
  'includes_transport': 'IconCar',
  'activity_type': 'IconActivity',
  'group_size': 'IconUsers',
  'min_age': 'IconUser',
  'max_age': 'IconUser',
  'instructor_included': 'IconUser',
  'equipment_included': 'IconBackpack',

  // Business & Pricing
  'providers': 'IconBuilding',
  'businessName': 'IconBuilding',
  'price': 'IconCurrency',
  'category': 'IconTag',
  'priority': 'IconStar',
  'status': 'IconCircleCheck',
  'stars': 'IconStar',

  // Time & Schedule
  'start_time': 'IconClock',
  'end_time': 'IconClock',
  'duration_hours': 'IconClock',
  'available_days': 'IconCalendar',

  // Features & Services
  'wifi': 'IconWifi',
  'parking': 'IconParking',
  'breakfast': 'IconCoffee',
  'air_conditioning': 'IconAirConditioning',
  'heating': 'IconFlame',
  'kitchen': 'IconChefHat',
  'tv': 'IconDeviceTv',
  'pool': 'IconPool',
  'gym': 'IconBarbell',
  'spa': 'IconMassage',
  'restaurant': 'IconFork',

  // Default fallback
  'default': 'IconSettings'
};

export const attributeDisplayNameMap: Record<string, string> = {
  // Location & Geography
  'origin_city': 'Origin',
  'destination_city': 'Destination',
  'max_guests': 'Paxs',
  'single_beds': 'Single Beds',
  'double_beds': 'Double Beds',
  'room_type': 'Room Type',
  'home_type': 'Property Type',
  'place_type': 'Place Type',
  'private_room': 'Private Room',
  'bath_private': 'Private Bathroom',
  'baths_number': 'Bathrooms',
  'spacial_view': 'View',
  'vehicle_type': 'Vehicle',
  'regular_schedule': 'Schedule Type',
  'includes_transport': 'Transport',
  'activity_type': 'Activity',
  'group_size': 'Group Size',
  'min_age': 'Min Age',
  'max_age': 'Max Age',
  'instructor_included': 'Instructor',
  'equipment_included': 'Equipment',
  'duration_hours': 'Duration (hrs)',
  'available_days': 'Available Days',
  'air_conditioning': 'AC',
  'businessName': 'Provider',

  // Add more as needed...
};

export const getAttributeIcon = (key: string): string => {
  return attributeIconMap[key] || attributeIconMap['default'];
};

export const isTier1Tenant = () => {
  const tenant = localTenant();
  return [1, 1598].includes(tenant?.id);
}

export const stripHtml = (html: string | null | undefined): string => {
  if (!html) return '';
  const withSpaces = html
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/(p|div|h[1-6]|li)>/gi, ' ');
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(withSpaces, 'text/html');
    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
  }
  return withSpaces.replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').replace(/\s+/g, ' ').trim();
};

export const sortPersons = (persons: Person[]) => {
  return [...persons].sort((a, b) => {
    const catA = a.category || '';
    const catB = b.category || '';
    if (catA !== catB) return catA.localeCompare(catB);
    return (a.name || '').localeCompare(b.name || '');
  });
}